"use client";

import {
  getCountActiveFilters,
  getFiltersUrlQuery,
  getResetFilters,
  ListingFilters,
  parseListingFilter,
  searchParamsToObject,
} from "@/lib/fetch/listing";
import React, { useEffect, useRef, useState } from "react";
import SearchControls from "./SearchControls";
import SearchFilters from "./filter/SearchFilters";
import SearchCount from "./SearchCount";
import SearchResults from "./SearchResults";
import { useQuery } from "@tanstack/react-query";
import { ListingSearchData } from "@/lib/types/search";
import { useSearchParams } from "next/navigation";
import SearchPagination from "../../../../../components/common/BasicPagination";
import SearchFilterChips from "./filter/SearchFilterChips";

type Props = {
  initialFilters: ListingFilters;
  locale: string;
};

const SearchView: React.FC<Props> = ({ initialFilters, locale }) => {
  const searchParams = useSearchParams();
  const loadedInitialUrlFilters = useRef(false);
  const [filters, setFilters] = useState(initialFilters);
  const [canSyncUrl, setCanSyncUrl] = useState(false);
  const [countFilters, setCountFilters] = useState(
    getCountActiveFilters(filters),
  );

  useEffect(() => {
    if (loadedInitialUrlFilters.current) {
      return;
    }

    loadedInitialUrlFilters.current = true;
    setFilters(parseListingFilter(searchParamsToObject(searchParams)));
    setCanSyncUrl(true);
  }, [searchParams]);

  useEffect(() => {
    if (!canSyncUrl) {
      return;
    }

    setCountFilters(getCountActiveFilters(filters));

    const { params } = getFiltersUrlQuery(filters);
    const nextSearch = params.toString();
    const currentSearch = window.location.search.slice(1);

    if (nextSearch !== currentSearch) {
      const nextUrl = `${window.location.pathname}${
        nextSearch ? `?${nextSearch}` : ""
      }`;

      window.history.replaceState(null, "", nextUrl);
    }
  }, [canSyncUrl, filters]);

  const { data, isLoading } = useQuery({
    queryKey: ["listing", "search", filters],
    enabled: canSyncUrl,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    refetchOnReconnect: false,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("locale", locale);

      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => {
            params.append(key, v);
          });
        } else if (value) {
          params.append(key, value.toString());
        }
      });

      const response = await fetch(
        `/api/site/public/listing?${params.toString()}`,
        {
          method: "GET",
        },
      );

      const data = (await response.json()) as {
        success: boolean;
        data: { items: ListingSearchData[]; count: number };
      };

      if (!response.ok) {
        return;
      }

      return data.data;
    },
  });

  const onReset = () => {
    setFilters(getResetFilters());
  };

  const getParamString = () => {
    const { params } = getFiltersUrlQuery(filters);
    return params.toString();
  };

  return (
    <div className="container flex flex-col gap-4 pt-5 pb-16">
      <SearchControls
        search={filters.search}
        setSearch={(search) => setFilters({ ...filters, search })}
        sort={filters.sort}
        setSort={(sort) => setFilters({ ...filters, sort })}
        paramString={getParamString()}
      />

      <SearchFilterChips
        filters={filters}
        setFilters={setFilters}
        countFilters={countFilters}
      >
        <SearchFilters
          filters={filters}
          setFilters={(filters) => setFilters(filters)}
          onReset={onReset}
          countFilters={countFilters}
          includeSearch={false}
        />
      </SearchFilterChips>

      <div className="lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start lg:gap-8">
        <aside className="google-anno-skip hidden lg:block">
          <div className="rounded-2xl border border-border pb-6">
            <SearchFilters
              filters={filters}
              setFilters={(filters) => setFilters(filters)}
              onReset={onReset}
              countFilters={countFilters}
              includeSearch={false}
            />
          </div>
        </aside>

        <div className="flex min-w-0 flex-col gap-4">
          <SearchCount count={data?.count || 0} isLoading={isLoading} />
          <SearchResults
            reset={onReset}
            items={data?.items || []}
            isLoading={isLoading}
          />
          {data && data.count !== 0 && !isLoading && (
            <SearchPagination
              currentPage={filters.page}
              perPage={filters.pageSize}
              totalPages={Math.ceil(data.count / filters.pageSize)}
              onPageChange={(page) => setFilters({ ...filters, page })}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchView;
